import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fetchClientTrades } from '../../services/vectorDashboardApi';
import {
  buildMonthCalendar, monthLabel, monthsWithTrades, yearsWithTrades,
} from './tradeCalendar';
import TradeMonthNavigator from './TradeMonthNavigator';
import ClientReportingConnectPrompt from './ClientReportingConnectPrompt';
import { CLIENT_REPORTING_DEMO_TRADES } from './clientReportingDemoPreview';

const DEMO_MONTH_KEY = '2026-09';

/**
 * Month grid of daily P&L, with weekly totals down the side.
 *
 * The equity curve answers "how did the account do"; this answers "which days
 * did it". A single red cell in an otherwise green month is the thing a trader
 * wants to find, and a line chart hides it.
 */

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** Compact money — cells are narrow and the exact cent is not the point here. */
function fmtCell(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n === 0) return '$0';
  const abs = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  if (abs >= 10000) return `${sign}$${(abs / 1000).toFixed(1)}k`;
  if (abs >= 1000) return `${sign}$${Math.round(abs).toLocaleString('en-US')}`;
  if (abs >= 100) return `${sign}$${abs.toFixed(0)}`;
  return `${sign}$${abs.toFixed(1)}`;
}

function toneOf(pnl) {
  if (pnl === null || pnl === undefined) return '';
  if (pnl > 0) return ' is-win';
  if (pnl < 0) return ' is-loss';
  return ' is-flat';
}

function CalendarGrid({ calendar, monthKey }) {
  const { totals } = calendar;

  return (
    <>
      <div className="vd-cal-head">
        <TradeMonthNavigator
          monthKey={monthKey}
          onMonthKeyChange={() => {}}
          monthsWithData={[monthKey]}
          yearsWithData={[String(monthKey).slice(0, 4)]}
        />

        <div className="vd-cal-stats">
          <span className="vd-cal-stats-label">Monthly stats:</span>
          <span className={`vd-cal-stat-pill${toneOf(totals.pnl)}`}>{fmtCell(totals.pnl)}</span>
          <span className="vd-cal-stat-pill">
            {totals.tradingDays} {totals.tradingDays === 1 ? 'day' : 'days'}
          </span>
        </div>
      </div>

      <div className="vd-cal-body">
        <div className="vd-cal-grid" role="grid" aria-label={`Daily results for ${monthLabel(monthKey)}`}>
          {WEEKDAYS.map((d) => (
            <div key={d} className="vd-cal-weekday" role="columnheader">{d}</div>
          ))}

          {calendar.weeks.flat().map((cell) => {
            if (!cell.inMonth) return <div key={cell.key} className="vd-cal-cell is-pad" />;
            const traded = cell.trades > 0;
            return (
              <div
                key={cell.key}
                role="gridcell"
                className={`vd-cal-cell${traded ? toneOf(cell.pnl) : ''}`}
              >
                <span className="vd-cal-daynum">{cell.dayOfMonth}</span>
                {traded ? (
                  <>
                    <span className="vd-cal-pnl">{fmtCell(cell.pnl)}</span>
                    <span className="vd-cal-meta">
                      {cell.trades} {cell.trades === 1 ? 'trade' : 'trades'}
                    </span>
                    {cell.winRate !== null ? (
                      <span className="vd-cal-meta">{cell.winRate.toFixed(1)}%</span>
                    ) : null}
                  </>
                ) : null}
              </div>
            );
          })}
        </div>

        <div className="vd-cal-weeks">
          {calendar.weekTotals.map((week) => (
            <div key={week.index} className={`vd-cal-week${week.tradingDays ? toneOf(week.pnl) : ''}`}>
              <span className="vd-cal-week-label">Week {week.index}</span>
              <span className="vd-cal-week-pnl">{week.tradingDays ? fmtCell(week.pnl) : '—'}</span>
              <span className="vd-cal-week-days">
                {week.tradingDays} {week.tradingDays === 1 ? 'day' : 'days'}
              </span>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

export default function ClientReportingTradeCalendar({
  clients = [],
  selectedClientId = null,
  refreshKey = 0,
  monthKey: controlledMonthKey = null,
  onMonthKeyChange = null,
  showDemoPreview = false,
  onConnectAction = null,
  connectPromptTitle = 'Add your account',
  connectPromptMessage = 'Connect a brokerage to see your daily P&L calendar here.',
  connectPromptActionLabel = 'Add / Sync Trades',
}) {
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(false);
  const [internalMonthKey, setInternalMonthKey] = useState(null);
  const isControlled = onMonthKeyChange != null;
  const monthKey = isControlled ? controlledMonthKey : internalMonthKey;

  const setMonthKey = useCallback((next) => {
    const value = typeof next === 'function' ? next(monthKey) : next;
    if (!value) return;
    if (isControlled) onMonthKeyChange(value);
    else setInternalMonthKey(value);
  }, [isControlled, monthKey, onMonthKeyChange]);

  const activeClient = useMemo(() => {
    if (selectedClientId && clients.some((c) => c.id === selectedClientId)) {
      return clients.find((c) => c.id === selectedClientId);
    }
    return clients[0] || null;
  }, [clients, selectedClientId]);

  // Fetches its own trades rather than sharing Recent Trades' list, which is
  // capped at the latest handful — a month grid needs the whole month.
  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (showDemoPreview) {
        setTrades(CLIENT_REPORTING_DEMO_TRADES);
        setLoading(false);
        return;
      }
      if (!activeClient?.dataSeriesName) {
        setTrades([]);
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const res = await fetchClientTrades(activeClient.dataSeriesName, 'All');
        if (!cancelled) setTrades(res.trades || []);
      } catch (err) {
        if (!cancelled) { console.error('[client-reporting-calendar]', err); setTrades([]); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [activeClient, refreshKey, showDemoPreview]);

  const months = useMemo(() => monthsWithTrades(trades), [trades]);
  const years = useMemo(() => yearsWithTrades(trades), [trades]);
  const anchoredForRef = useRef(null);

  /**
   * Anchor to the newest month that traded — ONCE per dataset.
   *
   * The first version re-anchored whenever the current month was not in
   * `months`, which meant stepping to a quiet month snapped straight back to
   * the newest one. Navigation was effectively frozen on whichever month
   * happened to contain the last trade. A quiet month is a legitimate thing to
   * look at; it just shows an empty grid.
   */
  useEffect(() => {
    if (showDemoPreview) {
      if (!monthKey) setMonthKey(DEMO_MONTH_KEY);
      return;
    }
    const signature = months.join('|');
    if (anchoredForRef.current === signature) return;
    anchoredForRef.current = signature;
    if (months.length) setMonthKey(months[0]);
  }, [months, setMonthKey, showDemoPreview, monthKey]);

  const effectiveMonthKey = showDemoPreview ? (monthKey || DEMO_MONTH_KEY) : monthKey;

  const calendar = useMemo(
    () => buildMonthCalendar(trades, effectiveMonthKey),
    [trades, effectiveMonthKey],
  );

  // Only hide when the ACCOUNT has no trades at all. A month with none is a
  // valid view — it is how you see that nothing happened.
  if (!showDemoPreview && !loading && !trades.length) return null;

  const isDemoPreview = showDemoPreview && !loading;

  if (isDemoPreview) {
    return (
      <section className="vd-panel vd-cal vd-cal-demo">
        <div className="vd-cal-demo-inner is-demo-preview">
          <CalendarGrid calendar={calendar} monthKey={effectiveMonthKey} />
          <div className="vd-cr-connect-overlay vd-cr-connect-overlay--inset">
            <ClientReportingConnectPrompt
              title={connectPromptTitle}
              message={connectPromptMessage}
              actionLabel={connectPromptActionLabel}
              onAction={onConnectAction}
              compact
            />
          </div>
        </div>
      </section>
    );
  }

  const { totals } = calendar;

  return (
    <section className="vd-panel vd-cal">
      <div className="vd-cal-head">
        <TradeMonthNavigator
          monthKey={effectiveMonthKey}
          onMonthKeyChange={setMonthKey}
          monthsWithData={months}
          yearsWithData={years}
        />

        <div className="vd-cal-stats">
          <span className="vd-cal-stats-label">Monthly stats:</span>
          <span className={`vd-cal-stat-pill${toneOf(totals.pnl)}`}>{fmtCell(totals.pnl)}</span>
          <span className="vd-cal-stat-pill">
            {totals.tradingDays} {totals.tradingDays === 1 ? 'day' : 'days'}
          </span>
        </div>
      </div>

      <div className="vd-cal-body">
        <div className="vd-cal-grid" role="grid" aria-label={`Daily results for ${monthLabel(effectiveMonthKey)}`}>
          {WEEKDAYS.map((d) => (
            <div key={d} className="vd-cal-weekday" role="columnheader">{d}</div>
          ))}

          {calendar.weeks.flat().map((cell) => {
            if (!cell.inMonth) return <div key={cell.key} className="vd-cal-cell is-pad" />;
            const traded = cell.trades > 0;
            return (
              <div
                key={cell.key}
                role="gridcell"
                className={`vd-cal-cell${traded ? toneOf(cell.pnl) : ''}`}
              >
                <span className="vd-cal-daynum">{cell.dayOfMonth}</span>
                {traded ? (
                  <>
                    <span className="vd-cal-pnl">{fmtCell(cell.pnl)}</span>
                    <span className="vd-cal-meta">
                      {cell.trades} {cell.trades === 1 ? 'trade' : 'trades'}
                    </span>
                    {cell.winRate !== null ? (
                      <span className="vd-cal-meta">{cell.winRate.toFixed(1)}%</span>
                    ) : null}
                  </>
                ) : null}
              </div>
            );
          })}
        </div>

        <div className="vd-cal-weeks">
          {calendar.weekTotals.map((week) => (
            <div key={week.index} className={`vd-cal-week${week.tradingDays ? toneOf(week.pnl) : ''}`}>
              <span className="vd-cal-week-label">Week {week.index}</span>
              <span className="vd-cal-week-pnl">{week.tradingDays ? fmtCell(week.pnl) : '—'}</span>
              <span className="vd-cal-week-days">
                {week.tradingDays} {week.tradingDays === 1 ? 'day' : 'days'}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
